/**
 * Hinweis zum Ablegen auf dem Home-Bildschirm.
 *
 * Auf iPadOS und iOS gibt es keinen Installationsknopf, den eine Web-App
 * selbst auslösen könnte: Safari bietet das ausschliesslich über das
 * Teilen-Blatt an. Mehr als der Weg dorthin ist technisch nicht möglich.
 * Deshalb zeigt die App einmalig, wo der Knopf sitzt.
 *
 * Der Hinweis erscheint nur, wenn die App im Browser läuft und noch nicht
 * vom Home-Bildschirm gestartet wurde.
 */
import { button, h } from '../ui/dom.ts';

const DISMISS_KEY = 'leadsheet:install-hint-dismissed';

/** iPadOS meldet sich seit Version 13 als Mac mit Mehrfachberührung. */
function isApplePortable(): boolean {
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

/** Läuft die App bereits im Vollbild vom Home-Bildschirm? */
function isStandalone(): boolean {
  const legacy = (navigator as Navigator & { standalone?: boolean }).standalone;
  if (legacy === true) return true;
  return window.matchMedia('(display-mode: standalone)').matches;
}

function wasDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    // Privater Modus oder gesperrter Speicher – dann eben jedes Mal zeigen.
    return false;
  }
}

function remember(): void {
  try {
    localStorage.setItem(DISMISS_KEY, '1');
  } catch {
    /* nicht schlimm */
  }
}

export function mountInstallHint(): void {
  if (!isApplePortable() || isStandalone() || wasDismissed()) return;

  const banner = h(
    'aside',
    { class: 'install-hint', attrs: { role: 'note' } },
    h(
      'div',
      { class: 'install-hint__text' },
      h('strong', { text: 'Als App ablegen' }),
      h('span', {
        text: 'Teilen-Symbol oben antippen, dann „Zum Home-Bildschirm“. Danach startet sie im Vollbild und ohne Verbindung.',
      }),
    ),
    button(
      'Verstanden',
      () => {
        remember();
        banner.remove();
      },
      { class: 'btn btn--small' },
    ),
  );

  document.body.append(banner);
}
